import React from "react";

import {
  CheckCircle2,
  Edit3,
  Globe2,
  Landmark,
  LoaderCircle,
  MapPin,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
  XCircle,
} from "lucide-react";

import { useAuth0 } from "@auth0/auth0-react";

import { useTranslation } from "react-i18next";

import { useToast } from "../../../components/feedback/ToastProvider";

import { EUROPEAN_COUNTRIES } from "../../../data/countries";

import "./AdminMarketRegionsPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

/* =========================================================*

*   EMPTY FORM*

*   ========================================================= */

const EMPTY_FORM = {
  country_code: "",

  code: "",

  name: "",

  google_place_id: "",

  google_primary_type: "",

  formatted_address: "",

  latitude: null,

  longitude: null,

  lender_organisation_ids: [],

  is_active: true,
};

/* =========================================================*

*   HELPERS*

*   ========================================================= */

function safeUpperCode(value = "") {
  return String(value || "")
    .trim()

    .toUpperCase()

    .replace(/[^A-Z0-9]+/g, "_")

    .replace(/^_+|_+$/g, "");
}

function loadGoogleMaps(apiKey) {
  if (!apiKey) {
    return Promise.resolve(null);
  }

  if (window.google?.maps?.places) {
    return Promise.resolve(window.google);
  }

  if (window.__legupAdminGoogleMapsPromise) {
    return window.__legupAdminGoogleMapsPromise;
  }

  window.__legupAdminGoogleMapsPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById("legup-google-maps-script");

    if (existing) {
      if (window.google?.maps?.places) {
        resolve(window.google);

        return;
      }

      existing.addEventListener("load", () => resolve(window.google), {
        once: true,
      });

      existing.addEventListener("error", reject, {
        once: true,
      });

      return;
    }

    const script = document.createElement("script");

    script.id = "legup-google-maps-script";

    script.src =
      "https://maps.googleapis.com/" +
      "maps/api/js" +
      `?key=${apiKey}` +
      "&libraries=places" +
      "&language=en";

    script.async = true;

    script.defer = true;

    script.onload = () => resolve(window.google);

    script.onerror = reject;

    document.head.appendChild(script);
  });

  return window.__legupAdminGoogleMapsPromise;
}

function getCountryLabel(code, t) {
  const fallback =
    EUROPEAN_COUNTRIES.find((country) => country.code === code)?.label ||
    code ||
    "—";

  if (!code) {
    return fallback;
  }

  return t(`common:countries.${code}`, {
    defaultValue: fallback,
  });
}

function normaliseErrorPayload(payload) {
  if (!payload || typeof payload !== "object") {
    return {};
  }

  const errors = {};

  Object.entries(payload.errors || payload).forEach(([key, value]) => {
    if (key === "detail") {
      return;
    }

    if (Array.isArray(value)) {
      errors[key] = value.join(" ");
    } else if (typeof value === "string") {
      errors[key] = value;
    }
  });

  return errors;
}

/* =========================================================*

*   STATUS*

*   ========================================================= */

function RegionStatus({ active, t }) {
  return (
    <span
      className={`admin-market-regions__status ${
        active ? "is-active" : "is-inactive"
      }`}
    >
      {active ? <CheckCircle2 size={14} /> : <XCircle size={14} />}

      {active
        ? t("marketRegions.status.active", "Active")
        : t("marketRegions.status.inactive", "Inactive")}
    </span>
  );
}

/* =========================================================*

*   SUMMARY CARD*

*   ========================================================= */

function SummaryCard({ label, value }) {
  return (
    <div className={"admin-market-regions__summary-card"}>
      <span>{label}</span>

      <strong>{Number(value || 0).toLocaleString()}</strong>
    </div>
  );
}

/* =========================================================*

*   PAGE*

*   ========================================================= */

export default function AdminMarketRegionsPage() {
  const { getAccessTokenSilently } = useAuth0();

  const { t } = useTranslation("admin-market-regions");

  const { showToast } = useToast();

  /* =======================================================*

*     DATA*

*     ======================================================= */

  const [regions, setRegions] = React.useState([]);

  const [summary, setSummary] = React.useState({});

  const [lenders, setLenders] = React.useState([]);

  /* =======================================================*

*     PAGE STATE*

*     ======================================================= */

  const [loading, setLoading] = React.useState(true);

  const [saving, setSaving] = React.useState(false);

  const [deletingRegionId, setDeletingRegionId] = React.useState(null);

  /* =======================================================*

*     FILTERS*

*     ======================================================= */

  const [searchTerm, setSearchTerm] = React.useState("");

  const [statusFilter, setStatusFilter] = React.useState("ALL");

  const [countryFilter, setCountryFilter] = React.useState("");

  /* =======================================================*

*     MODAL*

*     ======================================================= */

  const [modalOpen, setModalOpen] = React.useState(false);

  const [modalClosing, setModalClosing] = React.useState(false);

  const [editingRegion, setEditingRegion] = React.useState(null);

  const [form, setForm] = React.useState({
    ...EMPTY_FORM,
  });

  const [fieldErrors, setFieldErrors] = React.useState({});

  const [mapsError, setMapsError] = React.useState("");

  /* =======================================================*

*     REFS*

*     ======================================================= */

  const modalCloseTimerRef = React.useRef(null);

  const locationInputRef = React.useRef(null);

  const autocompleteRef = React.useRef(null);

  /* =======================================================*

*     LOAD LENDERS*

*     ======================================================= */

  const loadLenders = React.useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/admin/organisations/`,

        {
          headers: {
            Authorization: `Bearer ${token}`,

            Accept: "application/json",
          },
        },
      );

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          payload?.detail ||
            t(
              "marketRegions.errors.loadLenders",

              "Could not load lender organisations.",
            ),
        );
      }

      setLenders(
        Array.isArray(payload?.lender_organisations)
          ? payload.lender_organisations
          : [],
      );
    } catch (requestError) {
      setLenders([]);

      showToast({
        type: "error",

        title: t(
          "marketRegions.toast.lendersLoadErrorTitle",

          "Lenders unavailable",
        ),

        message:
          requestError?.message ||
          t(
            "marketRegions.errors.loadLenders",

            "Could not load lender organisations.",
          ),
      });
    }
  }, [getAccessTokenSilently, showToast, t]);

  React.useEffect(() => {
    loadLenders();
  }, [loadLenders]);

  /* =======================================================*

*     LOAD MARKET REGIONS*

*     ======================================================= */

  const loadRegions = React.useCallback(async () => {
    try {
      setLoading(true);

      const token = await getAccessTokenSilently();

      const params = new URLSearchParams();

      if (searchTerm.trim()) {
        params.set("search", searchTerm.trim());
      }

      if (countryFilter) {
        params.set("country_code", countryFilter);
      }

      if (statusFilter === "ACTIVE") {
        params.set("is_active", "true");
      } else if (statusFilter === "INACTIVE") {
        params.set("is_active", "false");
      }

      const query = params.toString();

      const response = await fetch(
        `${API_BASE}` +
          "/api/admin/" +
          "market-regions/" +
          (query ? `?${query}` : ""),

        {
          headers: {
            Authorization: `Bearer ${token}`,

            Accept: "application/json",
          },
        },
      );

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          payload?.detail ||
            t("marketRegions.errors.load", "Could not load market regions."),
        );
      }

      setRegions(Array.isArray(payload?.results) ? payload.results : []);

      setSummary(payload?.summary || {});
    } catch (requestError) {
      showToast({
        type: "error",

        title: t(
          "marketRegions.toast.loadErrorTitle",

          "Market regions unavailable",
        ),

        message:
          requestError?.message ||
          t("marketRegions.errors.load", "Could not load market regions."),
      });
    } finally {
      setLoading(false);
    }
  }, [
    countryFilter,

    getAccessTokenSilently,

    searchTerm,

    showToast,

    statusFilter,

    t,
  ]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      loadRegions();
    }, 220);

    return () => window.clearTimeout(timer);
  }, [loadRegions]);

  /* =======================================================*

*     CREATE*

*     ======================================================= */

  function openCreateModal() {
    setModalClosing(false);

    setEditingRegion(null);

    setForm({
      ...EMPTY_FORM,
    });

    setFieldErrors({});

    setMapsError("");

    setModalOpen(true);
  }

  /* =======================================================*

*     EDIT*

*     ======================================================= */

  function openEditModal(region) {
    setModalClosing(false);

    setEditingRegion(region);

    setForm({
      country_code: region.country_code || "",

      code: region.code || "",

      name: region.name || "",

      google_place_id: region.google_place_id || "",

      google_primary_type: region.google_primary_type || "",

      formatted_address: region.formatted_address || "",

      latitude: region.latitude ?? null,

      longitude: region.longitude ?? null,

      lender_organisation_ids: Array.isArray(region.participating_lender_ids)
        ? region.participating_lender_ids.map(Number)
        : Array.isArray(region.participating_lenders)
          ? region.participating_lenders

              .map((lender) => Number(lender.id))

              .filter(Boolean)
          : [],

      is_active: Boolean(region.is_active),
    });

    setFieldErrors({});

    setMapsError("");

    setModalOpen(true);
  }

  /* =======================================================*

*     CLOSE MODAL*

*     ======================================================= */

  function finishModalClose() {
    setModalOpen(false);

    setModalClosing(false);

    setEditingRegion(null);

    setForm({
      ...EMPTY_FORM,
    });

    setFieldErrors({});

    setMapsError("");
  }

  function startModalClose({ force = false } = {}) {
    if (saving && !force) {
      return;
    }

    setModalClosing(true);

    if (modalCloseTimerRef.current) {
      window.clearTimeout(modalCloseTimerRef.current);
    }

    modalCloseTimerRef.current = window.setTimeout(() => {
      finishModalClose();
    }, 220);
  }

  function closeModal() {
    startModalClose();
  }

  React.useEffect(() => {
    return () => {
      if (modalCloseTimerRef.current) {
        window.clearTimeout(modalCloseTimerRef.current);
      }
    };
  }, []);

  /* =======================================================*

*     FORM*

*     ======================================================= */

  function updateForm(name, value) {
    setForm((previous) => ({
      ...previous,

      [name]: value,
    }));

    setFieldErrors((previous) => ({
      ...previous,

      [name]: "",
    }));
  }

  function toggleLender(lenderId) {
    const numericId = Number(lenderId);

    setForm((previous) => {
      const currentIds = Array.isArray(previous.lender_organisation_ids)
        ? previous.lender_organisation_ids.map(Number)
        : [];

      const alreadySelected = currentIds.includes(numericId);

      return {
        ...previous,

        lender_organisation_ids: alreadySelected
          ? currentIds.filter((id) => id !== numericId)
          : [...currentIds, numericId],
      };
    });

    setFieldErrors((previous) => ({
      ...previous,

      lender_organisation_ids: "",
    }));
  }

  const availableLendersForForm = React.useMemo(() => {
    const byId = new Map();

    const currentAssigned = Array.isArray(editingRegion?.participating_lenders)
      ? editingRegion.participating_lenders
      : [];

    currentAssigned.forEach((lender) => {
      if (lender?.id) {
        byId.set(Number(lender.id), lender);
      }
    });

    lenders.forEach((lender) => {
      if (!lender?.id) {
        return;
      }

      if (lender.is_active === false) {
        return;
      }

      const lenderCountry = String(lender.country_code || "").toUpperCase();

      const formCountry = String(form.country_code || "").toUpperCase();

      if (formCountry && lenderCountry && lenderCountry !== formCountry) {
        return;
      }

      byId.set(Number(lender.id), lender);
    });

    return Array.from(byId.values()).sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || "")),
    );
  }, [editingRegion, form.country_code, lenders]);

  function handleCountryChange(value) {
    setForm((previous) => ({
      ...previous,

      country_code: value,

      google_place_id: "",

      google_primary_type: "",

      formatted_address: "",

      latitude: null,

      longitude: null,
    }));

    setFieldErrors((previous) => ({
      ...previous,

      country_code: "",
    }));

    setMapsError("");

    if (locationInputRef.current) {
      locationInputRef.current.value = "";
    }

    if (autocompleteRef.current && value) {
      autocompleteRef.current.setComponentRestrictions({
        country: value.toLowerCase(),
      });
    }
  }

  /* =======================================================*
   *     GOOGLE AUTOCOMPLETE*
   *     ======================================================= */

  React.useEffect(() => {
    if (!modalOpen || !GOOGLE_MAPS_API_KEY || !locationInputRef.current) {
      return undefined;
    }

    let cancelled = false;

    async function initialiseAutocomplete() {
      try {
        const google = await loadGoogleMaps(GOOGLE_MAPS_API_KEY);

        if (cancelled || !google || !locationInputRef.current) {
          return;
        }

        if (autocompleteRef.current) {
          return;
        }

        const options = {
          fields: [
            "formatted_address",
            "geometry",
            "place_id",
            "types",
            "name",
          ],

          types: ["(regions)"],
        };

        if (form.country_code) {
          options.componentRestrictions = {
            country: form.country_code.toLowerCase(),
          };
        }

        const autocomplete = new google.maps.places.Autocomplete(
          locationInputRef.current,
          options,
        );

        autocompleteRef.current = autocomplete;

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();

          if (!place?.place_id) {
            setMapsError(
              t(
                "marketRegions.errors.validGooglePlace",
                "Select a valid region from the Google suggestions.",
              ),
            );

            return;
          }

          const latitude = place.geometry?.location?.lat?.() ?? null;

          const longitude = place.geometry?.location?.lng?.() ?? null;

          setMapsError("");

          setForm((previous) => ({
            ...previous,

            name: previous.name || place.name || "",

            google_place_id: place.place_id || "",

            google_primary_type: place.types?.[0] || "",

            formatted_address: place.formatted_address || place.name || "",

            latitude,
            longitude,
          }));
        });
      } catch (error) {
        console.error("Google Maps autocomplete failed:", error);

        if (!cancelled) {
          setMapsError(
            t(
              "marketRegions.errors.maps",
              "Google Maps could not be loaded. You can still enter the market manually.",
            ),
          );
        }
      }
    }

    initialiseAutocomplete();

    return () => {
      cancelled = true;
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(
          autocompleteRef.current,
        );
      }

      autocompleteRef.current = null;
    };
  }, [modalOpen, t]);

  React.useEffect(() => {
    if (!autocompleteRef.current || !form.country_code) {
      return;
    }

    autocompleteRef.current.setComponentRestrictions({
      country: form.country_code.toLowerCase(),
    });
  }, [form.country_code]);

  /* =======================================================*

*     VALIDATION*

*     ======================================================= */

  function validateForm() {
    const errors = {};

    if (!form.country_code) {
      errors.country_code = t(
        "marketRegions.validation.country",

        "Country is required.",
      );
    }

    if (!form.code.trim()) {
      errors.code = t(
        "marketRegions.validation.code",

        "Region code is required.",
      );
    }

    if (!form.name.trim()) {
      errors.name = t(
        "marketRegions.validation.name",

        "Region name is required.",
      );
    }

    return errors;
  }

  /* =======================================================*

*     SAVE*

*     ======================================================= */

  async function saveRegion(event) {
    event.preventDefault();

    const errors = validateForm();

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    const isEditing = Boolean(editingRegion?.id);

    const regionName = form.name.trim();

    try {
      setSaving(true);

      const token = await getAccessTokenSilently();

      const body = {
        country_code: form.country_code,

        code: safeUpperCode(form.code),

        name: regionName,

        google_place_id: form.google_place_id || "",

        google_primary_type: form.google_primary_type || "",

        formatted_address: form.formatted_address || "",

        latitude: form.latitude,

        longitude: form.longitude,

        is_active: Boolean(form.is_active),
      };

      if (isEditing) {
        body.lender_organisation_ids = Array.isArray(
          form.lender_organisation_ids,
        )
          ? form.lender_organisation_ids.map(Number)
          : [];
      }

      const url = isEditing
        ? `${API_BASE}` +
          "/api/admin/" +
          "market-regions/" +
          `${editingRegion.id}/`
        : `${API_BASE}` + "/api/admin/" + "market-regions/";

      const response = await fetch(url, {
        method: isEditing ? "PATCH" : "POST",

        headers: {
          Authorization: `Bearer ${token}`,

          "Content-Type": "application/json",

          Accept: "application/json",
        },

        body: JSON.stringify(body),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        const backendErrors = normaliseErrorPayload(payload);

        if (Object.keys(backendErrors).length > 0) {
          setFieldErrors((previous) => ({
            ...previous,

            ...backendErrors,
          }));
        }

        throw new Error(
          payload?.detail ||
            t("marketRegions.errors.save", "Could not save the market region."),
        );
      }

      startModalClose({
        force: true,
      });

      await loadRegions();

      showToast({
        type: "success",

        title: isEditing
          ? t("marketRegions.toast.updatedTitle", "Market region updated")
          : t("marketRegions.toast.createdTitle", "Market region created"),

        message: isEditing
          ? t(
              "marketRegions.toast.updatedMessage",

              "{{name}} was updated successfully.",

              {
                name: regionName,
              },
            )
          : t(
              "marketRegions.toast.createdMessage",

              "{{name}} was added to the LEG-UP markets.",

              {
                name: regionName,
              },
            ),
      });
    } catch (requestError) {
      showToast({
        type: "error",

        title: isEditing
          ? t(
              "marketRegions.toast.updateErrorTitle",

              "Could not update market region",
            )
          : t(
              "marketRegions.toast.createErrorTitle",

              "Could not create market region",
            ),

        message:
          requestError?.message ||
          t("marketRegions.errors.save", "Could not save the market region."),
      });
    } finally {
      setSaving(false);
    }
  }

  /* =======================================================*

*     DELETE MARKET REGION*

*     ======================================================= */

  async function deleteRegion(region) {
    if (!region?.id) {
      return;
    }

    const confirmed = window.confirm(
      t(
        "marketRegions.delete.confirm",

        "Delete {{name}}? This is only possible if the market region is not already used by LEG-UP records.",

        {
          name: region.name || region.code || "this market region",
        },
      ),
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeletingRegionId(region.id);

      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/admin/market-regions/${region.id}/`,

        {
          method: "DELETE",

          headers: {
            Authorization: `Bearer ${token}`,

            Accept: "application/json",
          },
        },
      );

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          payload?.detail ||
            t(
              "marketRegions.errors.delete",

              "Could not delete the market region.",
            ),
        );
      }

      showToast({
        type: "success",

        title: t(
          "marketRegions.toast.deletedTitle",

          "Market region deleted",
        ),

        message: t(
          "marketRegions.toast.deletedMessage",

          "{{name}} was deleted successfully.",

          {
            name: region.name || region.code || "Market region",
          },
        ),
      });

      await loadRegions();
    } catch (requestError) {
      showToast({
        type: "error",

        title: t(
          "marketRegions.toast.deleteErrorTitle",

          "Could not delete market region",
        ),

        message:
          requestError?.message ||
          t(
            "marketRegions.errors.delete",

            "Could not delete the market region.",
          ),
      });
    } finally {
      setDeletingRegionId(null);
    }
  }

  /* =======================================================*

*     RENDER*

*     ======================================================= */

  return (
    <main className={"admin-market-regions"}>
      {/* ===================================================*

*          HEADER*

*          =================================================== */}

      <header className={"admin-market-regions__header"}>
        <div>
          <span className={"admin-market-regions__eyebrow"}>
            {t("marketRegions.eyebrow", "Platform configuration")}
          </span>

          <h1>{t("marketRegions.title", "Market Regions")}</h1>

          <p>
            {t(
              "marketRegions.subtitle",

              "Define the LEG-UP markets available to homebuyers, lenders and One-Stop Shops.",
            )}
          </p>
        </div>

        <button
          type="button"
          className={"admin-market-regions__primary-btn"}
          onClick={openCreateModal}
        >
          <Plus size={17} />

          {t("marketRegions.actions.add", "Add market region")}
        </button>
      </header>

      {/* ===================================================*

*          SUMMARY*

*          =================================================== */}

      <section className={"admin-market-regions__summary"}>
        <SummaryCard
          label={t("marketRegions.summary.total", "Total markets")}
          value={summary.total}
        />

        <SummaryCard
          label={t("marketRegions.summary.active", "Active")}
          value={summary.active}
        />

        <SummaryCard
          label={t("marketRegions.summary.inactive", "Inactive")}
          value={summary.inactive}
        />

        <SummaryCard
          label={t(
            "marketRegions.summary.participatingLenders",

            "With participating lenders",
          )}
          value={summary.with_participating_lenders ?? 0}
        />
      </section>

      {/* ===================================================*

*          PANEL*

*          =================================================== */}

      <section className={"admin-market-regions__panel"}>
        <div className={"admin-market-regions__toolbar"}>
          <div className={"admin-market-regions__search"}>
            <Search size={16} />

            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t(
                "marketRegions.search",

                "Search by region, code or address",
              )}
            />
          </div>

          <select
            value={countryFilter}
            onChange={(event) => setCountryFilter(event.target.value)}
          >
            <option value="">
              {t("marketRegions.filters.allCountries", "All countries")}
            </option>

            {EUROPEAN_COUNTRIES.map((country) => (
              <option key={country.code} value={country.code}>
                {t(`common:countries.${country.code}`, {
                  defaultValue: country.label,
                })}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="ALL">
              {t("marketRegions.filters.allStatuses", "All statuses")}
            </option>

            <option value="ACTIVE">
              {t("marketRegions.status.active", "Active")}
            </option>

            <option value="INACTIVE">
              {t("marketRegions.status.inactive", "Inactive")}
            </option>
          </select>

          <button
            type="button"
            className={"admin-market-regions__refresh-btn"}
            onClick={loadRegions}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? "is-spinning" : ""} />

            {t("marketRegions.actions.refresh", "Refresh")}
          </button>
        </div>

        {/* =================================================*

*            CONTENT*

*            ================================================= */}

        {loading ? (
          <div className={"admin-market-regions__loading"}>
            <LoaderCircle size={24} className={"is-spinning"} />

            <span>
              {t("marketRegions.loading", "Loading market regions...")}
            </span>
          </div>
        ) : regions.length === 0 ? (
          <div className={"admin-market-regions__empty"}>
            <Globe2 size={26} />

            <strong>
              {t("marketRegions.empty.title", "No market regions")}
            </strong>

            <span>
              {t("marketRegions.empty.text", "Create the first LEG-UP market.")}
            </span>
          </div>
        ) : (
          <div className={"admin-market-regions__table-wrap"}>
            <table className={"admin-market-regions__table"}>
              <thead>
                <tr>
                  <th>{t("marketRegions.table.country", "Country")}</th>

                  <th>{t("marketRegions.table.market", "Market")}</th>

                  <th>{t("marketRegions.table.code", "Code")}</th>

                  <th>
                    {t(
                      "marketRegions.table.participatingLenders",

                      "Participating lenders",
                    )}
                  </th>

                  <th>
                    {t("marketRegions.table.location", "Google location")}
                  </th>

                  <th>{t("marketRegions.table.status", "Status")}</th>

                  <th
                    aria-label={t("marketRegions.table.actions", "Actions")}
                  />
                </tr>
              </thead>

              <tbody>
                {regions.map((region) => {
                  const lenderNames = Array.isArray(
                    region.participating_lenders,
                  )
                    ? region.participating_lenders

                        .map((lender) => lender.name)

                        .filter(Boolean)
                    : [];

                  return (
                    <tr key={region.id}>
                      <td>
                        <span className={"admin-market-regions__country"}>
                          {getCountryLabel(region.country_code, t)}
                        </span>
                      </td>

                      <td>
                        <strong>{region.name}</strong>
                      </td>

                      <td>
                        <code>{region.code}</code>
                      </td>

                      <td>
                        {lenderNames.length > 0 ? (
                          <div className="admin-market-regions__lenders-list">
                            {lenderNames.slice(0, 3).map((lenderName) => (
                              <span
                                key={lenderName}
                                className="admin-market-regions__lender"
                              >
                                <Landmark size={13} />

                                {lenderName}
                              </span>
                            ))}

                            {lenderNames.length > 3 ? (
                              <span
                                className="

                                  admin-market-regions__lender

                                  admin-market-regions__lender--more

                                    "
                                title={lenderNames.slice(3).join(", ")}
                              >
                                +{lenderNames.length - 3}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <span className="admin-market-regions__muted">
                            {t("marketRegions.table.noLenders", "None")}
                          </span>
                        )}
                      </td>

                      <td>
                        {region.formatted_address ? (
                          <span className={"admin-market-regions__location"}>
                            <MapPin size={14} />

                            {region.formatted_address}
                          </span>
                        ) : (
                          <span className={"admin-market-regions__muted"}>
                            {t("marketRegions.location.manual", "Manual")}
                          </span>
                        )}
                      </td>

                      <td>
                        <RegionStatus active={region.is_active} t={t} />
                      </td>

                      <td className={"admin-market-regions__actions-cell"}>
                        <button
                          type="button"
                          className={"admin-market-regions__icon-btn"}
                          onClick={() => openEditModal(region)}
                          aria-label={t("marketRegions.actions.edit", "Edit")}
                          title={t("marketRegions.actions.edit", "Edit")}
                        >
                          <Edit3 size={16} />
                        </button>

                        <button
                          type="button"
                          className={
                            "admin-market-regions__icon-btn " +
                            "admin-market-regions__icon-btn--danger"
                          }
                          onClick={() => deleteRegion(region)}
                          disabled={deletingRegionId === region.id}
                          aria-label={t(
                            "marketRegions.actions.delete",

                            "Delete",
                          )}
                          title={t(
                            "marketRegions.actions.delete",

                            "Delete",
                          )}
                        >
                          {deletingRegionId === region.id ? (
                            <LoaderCircle size={16} className={"is-spinning"} />
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ===================================================*

*          MODAL*

*          =================================================== */}

      {modalOpen ? (
        <div
          className={`admin-market-regions__modal-backdrop ${
            modalClosing ? "is-closing" : ""
          }`}
          role="presentation"
        >
          <div
            className={"admin-market-regions__modal"}
            role="dialog"
            aria-modal="true"
            aria-labelledby={"market-region-modal-title"}
          >
            <div className={"admin-market-regions__modal-header"}>
              <div>
                <span>
                  {editingRegion
                    ? t("marketRegions.modal.editEyebrow", "Edit configuration")
                    : t("marketRegions.modal.addEyebrow", "New configuration")}
                </span>

                <h2 id={"market-region-modal-title"}>
                  {editingRegion
                    ? t("marketRegions.modal.editTitle", "Edit market region")
                    : t("marketRegions.modal.addTitle", "Add market region")}
                </h2>
              </div>

              <button
                type="button"
                className={"admin-market-regions__modal-close"}
                onClick={closeModal}
                disabled={saving}
                aria-label={t("marketRegions.actions.close", "Close")}
              >
                <X size={19} />
              </button>
            </div>

            <form
              onSubmit={saveRegion}
              className={"admin-market-regions__form"}
            >
              <div className={"admin-market-regions__form-grid"}>
                {/* =========================================*

*                    COUNTRY*

*                    ========================================= */}

                <label className={"admin-market-regions__field"}>
                  <span>{t("marketRegions.fields.country", "Country")} *</span>

                  <select
                    value={form.country_code}
                    onChange={(event) =>
                      handleCountryChange(event.target.value)
                    }
                    disabled={Boolean(editingRegion)}
                    className={fieldErrors.country_code ? "has-error" : ""}
                  >
                    <option value="">
                      {t(
                        "marketRegions.fields.selectCountry",

                        "Select country",
                      )}
                    </option>

                    {EUROPEAN_COUNTRIES.map((country) => (
                      <option key={country.code} value={country.code}>
                        {t(`common:countries.${country.code}`, {
                          defaultValue: country.label,
                        })}
                      </option>
                    ))}
                  </select>

                  {fieldErrors.country_code ? (
                    <small className={"admin-market-regions__field-error"}>
                      {fieldErrors.country_code}
                    </small>
                  ) : null}
                </label>

                {/* =========================================*

*                    CODE*

*                    ========================================= */}

                <label className={"admin-market-regions__field"}>
                  <span>{t("marketRegions.fields.code", "Region code")} *</span>

                  <input
                    value={form.code}
                    onChange={(event) =>
                      updateForm("code", safeUpperCode(event.target.value))
                    }
                    placeholder={"FLANDERS"}
                    disabled={Boolean(editingRegion)}
                    className={fieldErrors.code ? "has-error" : ""}
                  />

                  {fieldErrors.code ? (
                    <small className={"admin-market-regions__field-error"}>
                      {fieldErrors.code}
                    </small>
                  ) : null}
                </label>

                {/* =========================================*

*                    NAME*

*                    ========================================= */}

                <label
                  className={
                    "admin-market-regions__field " +
                    "admin-market-regions__field--full"
                  }
                >
                  <span>{t("marketRegions.fields.name", "Region name")} *</span>

                  <input
                    value={form.name}
                    onChange={(event) => updateForm("name", event.target.value)}
                    placeholder={t(
                      "marketRegions.fields.namePlaceholder",

                      "e.g. Flanders",
                    )}
                    className={fieldErrors.name ? "has-error" : ""}
                  />

                  {fieldErrors.name ? (
                    <small className={"admin-market-regions__field-error"}>
                      {fieldErrors.name}
                    </small>
                  ) : null}
                </label>

                {editingRegion ? (
                  <div
                    className={
                      "admin-market-regions__field " +
                      "admin-market-regions__field--full"
                    }
                  >
                    <span>
                      {t(
                        "marketRegions.fields.lenders",

                        "Participating lenders",
                      )}
                    </span>

                    <small>
                      {t(
                        "marketRegions.fields.lendersHint",

                        "Select all lender organisations that participate in this market region.",
                      )}
                    </small>

                    <div className={"admin-market-regions__lender-selector"}>
                      {availableLendersForForm.length > 0 ? (
                        availableLendersForForm.map((lender) => {
                          const lenderId = Number(lender.id);

                          const checked = Array.isArray(
                            form.lender_organisation_ids,
                          )
                            ? form.lender_organisation_ids

                                .map(Number)

                                .includes(lenderId)
                            : false;

                          return (
                            <label
                              key={lender.id}
                              className={
                                "admin-market-regions__lender-option" +
                                (checked ? " is-selected" : "")
                              }
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleLender(lenderId)}
                              />

                              <span>
                                <Landmark size={15} />

                                <strong>
                                  {lender.name || `Lender ${lender.id}`}
                                </strong>
                              </span>
                            </label>
                          );
                        })
                      ) : (
                        <div className={"admin-market-regions__muted"}>
                          {t(
                            "marketRegions.fields.noLenders",

                            "No active lender organisations are available for this country.",
                          )}
                        </div>
                      )}
                    </div>

                    {fieldErrors.lender_organisation_ids ? (
                      <small className={"admin-market-regions__field-error"}>
                        {fieldErrors.lender_organisation_ids}
                      </small>
                    ) : null}
                  </div>
                ) : null}

                {/* =========================================*

*                    GOOGLE LOCATION*

*                    ========================================= */}

                <label
                  className={
                    "admin-market-regions__field " +
                    "admin-market-regions__field--full"
                  }
                >
                  <span>
                    {t(
                      "marketRegions.fields.googleLocation",

                      "Google location (optional)",
                    )}
                  </span>

                  <div className={"admin-market-regions__location-input"}>
                    <MapPin size={16} />

                    <input
                      ref={locationInputRef}
                      defaultValue={form.formatted_address || ""}
                      placeholder={t(
                        "marketRegions.fields.googlePlaceholder",

                        "Search for the geographic region",
                      )}
                      disabled={!GOOGLE_MAPS_API_KEY || !form.country_code}
                    />
                  </div>

                  {!GOOGLE_MAPS_API_KEY ? (
                    <small>
                      {t(
                        "marketRegions.fields.mapsOptional",

                        "Google Maps key is not configured. The market can still be created manually.",
                      )}
                    </small>
                  ) : null}

                  {mapsError ? (
                    <small className={"admin-market-regions__field-error"}>
                      {mapsError}
                    </small>
                  ) : null}

                  {form.google_place_id ? (
                    <small className={"admin-market-regions__google-meta"}>
                      {form.google_primary_type || "Google place"}

                      {" · "}

                      {form.google_place_id}
                    </small>
                  ) : null}
                </label>

                {/* =========================================*

*                    ACTIVE*

*                    ========================================= */}

                <label
                  className={
                    "admin-market-regions__toggle " +
                    "admin-market-regions__field--full"
                  }
                >
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(event) =>
                      updateForm("is_active", event.target.checked)
                    }
                  />

                  <span className={"admin-market-regions__toggle-control"} />

                  <span>
                    <strong>
                      {t("marketRegions.fields.active", "Active market")}
                    </strong>

                    <small>
                      {t(
                        "marketRegions.fields.activeHint",

                        "Active markets are available for LEG-UP registration and platform workflows.",
                      )}
                    </small>
                  </span>
                </label>
              </div>

              {/* =============================================*
               *                  ACTIONS*
               *                  ============================================= */}

              <div className={"admin-market-regions__modal-actions"}>
                <button
                  type="button"
                  className={"admin-market-regions__secondary-btn"}
                  onClick={closeModal}
                  disabled={saving}
                >
                  {t("marketRegions.actions.cancel", "Cancel")}
                </button>

                <button
                  type="submit"
                  className={"admin-market-regions__primary-btn"}
                  disabled={saving}
                >
                  {saving ? (
                    <LoaderCircle size={16} className={"is-spinning"} />
                  ) : null}

                  {editingRegion
                    ? t("marketRegions.actions.save", "Save changes")
                    : t("marketRegions.actions.create", "Create market")}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </main>
  );
}
